import React, { useState, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, IconButton, Typography, Stack, CircularProgress } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function PDFViewer({ pdfUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const goToPrevPage = () => setPageNumber((p) => Math.max(1, p - 1));
  const goToNextPage = () => setPageNumber((p) => Math.min(numPages, p + 1));
  const zoomIn = () => setScale((s) => Math.min(3, s + 0.2));
  const zoomOut = () => setScale((s) => Math.max(0.5, s - 0.2));

  const toggleFullscreen = () => {
    const el = document.getElementById("pdf-viewer-container");
    if (!isFullscreen) {
      el?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const handleContextMenu = useCallback((e) => e.preventDefault(), []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "p")) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (!pdfUrl) return null;

  return (
    <Box
      id="pdf-viewer-container"
      onContextMenu={handleContextMenu}
      sx={{
        userSelect: "none",
        WebkitUserSelect: "none",
        bgcolor: isFullscreen ? "#333" : "#f5f5f5",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          py: 1,
          px: 2,
          bgcolor: "#222",
          color: "#fff",
          width: "100%",
          justifyContent: "center",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <IconButton onClick={goToPrevPage} disabled={pageNumber <= 1} sx={{ color: "#fff" }}>
          <NavigateBeforeIcon />
        </IconButton>
        <Typography variant="body2">
          {pageNumber} / {numPages || "-"}
        </Typography>
        <IconButton onClick={goToNextPage} disabled={pageNumber >= numPages} sx={{ color: "#fff" }}>
          <NavigateNextIcon />
        </IconButton>
        <IconButton onClick={zoomOut} sx={{ color: "#fff" }}>
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={zoomIn} sx={{ color: "#fff" }}>
          <ZoomInIcon />
        </IconButton>
        <IconButton onClick={toggleFullscreen} sx={{ color: "#fff" }}>
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Stack>
      <Box sx={{ overflow: "auto", flex: 1, py: 2, display: "flex", justifyContent: "center" }}>
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<CircularProgress sx={{ mt: 10 }} />}
          error={<Typography sx={{ mt: 10 }}>Failed to load PDF</Typography>}
        >
          <Page pageNumber={pageNumber} scale={scale} />
        </Document>
      </Box>
    </Box>
  );
}
